import React, { useContext, useState } from "react";
import { UserContext } from "../../../context/UserContext";
import UploadPrescriptionSlider from "./UploadPrescriptionSlider";
import CallPopup from "../../common/CallPopup.js";
import { usePopupLock } from "../../../context/PopupLockContext";
import { Flame, Star, Upload, Phone, FileText, Headphones } from "lucide-react";

export default function PrescriptionOptions({ setShowLogin }) {
  const { user } = useContext(UserContext);
  const { openLock, closeLock } = usePopupLock();

  const [showUpload, setShowUpload] = useState(false);
  const [showCallPopup, setShowCallPopup] = useState(false);

  const isLoggedIn = !!user;

  const handleUploadClick = () => {
    if (!isLoggedIn) return setShowLogin?.(true);
    setShowUpload(true);
    openLock("call");
  };

  const handleCallClick = () => {
    setShowCallPopup(true);
    openLock("call");
  };

  return (
    <>
      <div className="px-4 md:px-8 py-6 bg-gradient-to-br from-gray-50 via-white to-red-50/30 rounded-xl shadow-lg mt-3">
        
        {/* Header Section */}
        <div className="text-center mb-5">
         
          
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-extrabold">
            <span className="bg-gradient-to-r from-gray-800 to-gray-900 bg-clip-text text-transparent">
              Need
            </span>{" "}
            <span className="bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent">
              Medical Assistance?
            </span>
          </h2>
          
          <p className="text-gray-500 text-sm mt-2 max-w-md mx-auto">
            Upload your prescription or call our experts for instant help
          </p>
          
        
        </div>

        {/* CARDS - Upload & Call (Smaller Height) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          
          {/* UPLOAD CARD */}
          <div className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            {/* Top Border Animation */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            
            <div className="p-4 text-center">
              {/* Icon - Smaller */}
              <div className="w-12 h-12 mx-auto bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                <Upload size={22} className="text-red-600" />
              </div>
              
              <h3 className="text-base font-bold text-gray-800 group-hover:text-red-600 transition-colors duration-300">
                Upload Prescription
              </h3>
              
              <p className="text-[11px] text-gray-500 mt-1">
                Upload doctor's prescription & get medicines delivered
              </p>
              
              <div className="flex items-center justify-center gap-2 mt-2 text-[9px] text-green-600">
                <span className="flex items-center gap-1">
                  <span className="text-green-500">✓</span>
                  <span>Free pickup</span>
                </span>
                <span className="w-0.5 h-0.5 bg-gray-300 rounded-full"></span>
                <span className="flex items-center gap-1">
                  <span className="text-green-500">✓</span>
                  <span>Fast delivery</span>
                </span>
              </div>
              
              <button
                onClick={handleUploadClick}
                className="mt-3 w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-2 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-md hover:shadow-lg"
              >
                Upload Now
              </button>
            </div>
          </div>

          {/* CALL CARD */}
          <div className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
            {/* Top Border Animation */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 to-red-600 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"></div>
            
            <div className="p-4 text-center">
              {/* Icon - Smaller */}
              <div className="w-12 h-12 mx-auto bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform duration-300">
                <Phone size={22} className="text-red-600" />
              </div>
              
              <h3 className="text-base font-bold text-gray-800 group-hover:text-red-600 transition-colors duration-300">
                Call Expert
              </h3>
              
              <p className="text-[11px] text-gray-500 mt-1">
                Get instant medical advice from our healthcare experts
              </p>
              
              <div className="flex items-center justify-center gap-2 mt-2 text-[9px] text-red-600">
                <span className="flex items-center gap-1">
                  <span className="text-red-500">✓</span>
                  <span>24/7 Available</span>
                </span>
                <span className="w-0.5 h-0.5 bg-gray-300 rounded-full"></span>
                <span className="flex items-center gap-1">
                  <span className="text-red-500">✓</span>
                  <span>Free consult</span>
                </span>
              </div>
              
              <button
                onClick={handleCallClick}
                className="mt-3 w-full bg-gradient-to-r from-red-500 to-red-600 text-white py-2 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-300 shadow-md hover:shadow-lg flex items-center justify-center gap-2"
              >
                <Headphones size={12} />
                Call Now
              </button>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-5 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
                <span className="text-green-600 text-[9px] font-bold">✓</span>
              </div>
              <span>Free Prescription Upload</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
                <span className="text-blue-600 text-[9px] font-bold">⚡</span>
              </div>
              <span>24/7 Expert Support</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-purple-100 rounded-full flex items-center justify-center">
                <span className="text-purple-600 text-[9px] font-bold">🏆</span>
              </div>
              <span>Safe & Secure</span>
            </div>
            <div className="flex items-center justify-center gap-1.5 text-[10px] text-gray-600 bg-white/50 rounded-lg p-1.5 hover:bg-white transition-all duration-300">
              <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
                <span className="text-red-600 text-[9px] font-bold">❤️</span>
              </div>
              <span>Trusted by Millions</span>
            </div>
          </div>
        </div>
      </div>

      {/* POPUPS */}
      {showUpload && (
        <UploadPrescriptionSlider
          isOpen={showUpload}
          onClose={() => {
            setShowUpload(false);
            closeLock("call");
          }}
        />
      )}

      {showCallPopup && (
        <CallPopup
          isOpen={showCallPopup}
          onClose={() => {
            setShowCallPopup(false);
            closeLock("call");
          }}
          user={user}
        />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.05); }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
      `}</style>
    </>
  );
}